/**
 * App Marketplace API (docs/app_marketplace.md).
 *
 *   /marketplace/public/*   anyone, signed out: browse, an app's page, limits
 *   /marketplace/installs/* a signed-in organization: install + My apps
 *   /marketplace/sell/*     a project manager on Pro or above
 *   /marketplace/admin/*    instance admins
 */
import api from './client'
import type { CheckoutOrder } from './billing'

const BASE = '/marketplace'

export type Basis = 'person' | 'server'

export interface AppCard {
  slug: string
  name: string
  tagline: string
  category: string
  icon_color: string
  initials: string
  publisher: string
  currency: string
  from_price_minor: number | null
  from_basis: Basis | null
  trial_days: number
  published_at: string | null
}

export interface LimitRow { key: string; label: string; help: string; tech?: string; value: string }

export interface AppDetail extends AppCard {
  description: string
  support_email: string
  screenshots: { id: string; caption: string; url: string }[]
  person: { price_minor: number } | null
  server: { id: string; name: string; max_people: number; price_minor: number }[]
  trial: { days: number; people: number } | null
  retention: { readonly_days: number; delete_after_days: number }
  grace_days: number
}

export interface PublicConfig {
  enabled: boolean
  categories: string[]
  grace_days: number
  retention: { readonly_days: number; delete_after_days: number }
}

export async function getPublicConfig(): Promise<PublicConfig> {
  const { data } = await api.get(`${BASE}/public/config`)
  return data
}

export async function listApps(params?: { q?: string; category?: string }): Promise<{ apps: AppCard[] }> {
  const { data } = await api.get(`${BASE}/public/apps`, { params })
  return data
}

export async function getApp(slug: string): Promise<AppDetail> {
  const { data } = await api.get(`${BASE}/public/apps/${encodeURIComponent(slug)}`)
  return data
}

export async function getAppLimits(slug: string, basis: Basis, seats?: number, size?: string): Promise<{ limits: LimitRow[] }> {
  const { data } = await api.get(`${BASE}/public/apps/${encodeURIComponent(slug)}/limits`, {
    params: { basis, seats, size },
  })
  return data
}

// ── install + My apps ───────────────────────────────────────────────────────

export interface Quote {
  currency: string
  basis: Basis
  seats: number | null
  size: string | null
  unit_price_minor: number
  monthly_minor: number
  trial: { days: number; people: number; ends_at: string } | null
  due_today_minor: number
  limits: LimitRow[]
  grace_days: number
  wallet_balance_minor?: number
  installer_role?: 'admin' | 'project_owner' | null
  organization?: { tenant_id: string; name: string | null }
  own_app?: boolean
  existing_subscription_id?: number | null
  app: AppCard
}

export interface InstallStep { key: string; label: string; state: 'pending' | 'running' | 'done' | 'failed' | 'skipped'; note?: string }

export interface Subscription {
  id: number
  status: 'installing' | 'failed' | 'trial' | 'active' | 'past_due' | 'ended' | 'deleted'
  listing_slug: string | null
  name: string
  icon_color: string | null
  initials: string
  publisher: string
  support_email: string | null
  basis: Basis
  seats: number | null
  size: string | null
  size_name: string | null
  currency: string
  unit_price_minor: number
  monthly_minor: number
  pending: { basis: Basis | null; seats: number | null; size: string | null; price_minor: number | null; price_effective_at: string | null }
  trial_ends_at: string | null
  trial_people: number | null
  period_start: string | null
  period_end: string | null
  next_charge_at: string | null
  cancel_at_period_end: boolean
  grace_until: string | null
  ended_at: string | null
  ended_reason: string | null
  readonly_until: string | null
  delete_at: string | null
  deleted_at: string | null
  delete_note: string | null
  can_export: boolean
  can_resume: boolean
  install: { steps?: InstallStep[]; error?: string | null }
  open_url: string
  installed_at: string | null
  wallet_balance_minor: number
  grace_days: number
  // detail only
  usage?: { key: string; label: string; used: number | null; of: number | null; unit: string; pct: number | null }[]
  limits?: LimitRow[]
  people?: number
  charges?: { id: number; date: string; description: string; amount_minor: number; kind: string; invoice_id: number | null; invoice_number: string | null }[]
  retention?: { readonly_days: number; delete_after_days: number; trial_data_days: number }
}

export async function getQuote(slug: string, basis: Basis, seats?: number, size?: string): Promise<Quote> {
  const { data } = await api.get(`${BASE}/installs/quote`, { params: { slug, basis, seats, size } })
  return data
}

export interface InstallRequest {
  slug: string
  basis: Basis
  seats?: number
  size?: string
  sample_data?: boolean
  invites?: string[]
  use_trial?: boolean
}

export async function installApp(body: InstallRequest): Promise<Subscription> {
  const { data } = await api.post(`${BASE}/installs`, body)
  return data
}

export async function listInstalls(): Promise<{ role: string | null; items: Subscription[]; monthly_total: Record<string, number>; tenant_id: string }> {
  const { data } = await api.get(`${BASE}/installs`)
  return data
}

export async function getInstall(id: number): Promise<Subscription> {
  const { data } = await api.get(`${BASE}/installs/${id}`)
  return data
}

export async function changePlan(id: number, body: { basis: Basis; seats?: number | null; size?: string | null }) {
  const { data } = await api.post(`${BASE}/installs/${id}/plan`, body)
  return data as { result: { charged_minor: number; effective: string; effective_at?: string }; subscription: Subscription }
}

export async function cancelInstall(id: number, reason?: string): Promise<Subscription> {
  const { data } = await api.post(`${BASE}/installs/${id}/cancel`, { reason })
  return data
}

export async function keepInstall(id: number): Promise<Subscription> {
  const { data } = await api.post(`${BASE}/installs/${id}/keep`)
  return data
}

export async function resumeInstall(id: number): Promise<Subscription> {
  const { data } = await api.post(`${BASE}/installs/${id}/resume`)
  return data
}

export async function retryInstall(id: number): Promise<Subscription> {
  const { data } = await api.post(`${BASE}/installs/${id}/retry`)
  return data
}

export async function invitePerson(id: number, email: string) {
  const { data } = await api.post(`${BASE}/installs/${id}/invite`, { email })
  return data as { invited: boolean; subscription: Subscription }
}

export function exportUrl(id: number): string {
  return `${api.defaults.baseURL}${BASE}/installs/${id}/export`
}

/** A server path under /api (screenshots) → a URL that honours the app's base path. */
export function assetUrl(path: string): string {
  return path.startsWith('/api/') ? `${api.defaults.baseURL}${path.slice(4)}` : path
}

export function adminScreenshotUrl(listingId: number, shotId: string): string {
  return `${api.defaults.baseURL}${BASE}/admin/screenshots/${listingId}/${shotId}`
}

export function installInvoiceUrl(id: number, number: string): string {
  return `${api.defaults.baseURL}${BASE}/installs/${id}/invoices/${encodeURIComponent(number)}`
}

// ── publisher ───────────────────────────────────────────────────────────────

export interface Judgement {
  level: 'ok' | 'warn' | 'error'
  message: string
  price_minor: number
  cost_minor: number
  fee_minor: number
  earn_minor: number
  break_even_minor: number
}

export interface Pricing {
  person: { enabled: boolean; price_minor: number | null }
  server: Record<string, { enabled: boolean; price_minor: number | null }>
  trial: { days: number; people: number }
}

export interface TrialBudget {
  currency: string
  balance_minor: number
  topped_up_minor: number
  used_minor: number
  withdrawn_minor: number
  held_minor: number
  available_minor: number
  per_trial_minor: number
  trials_left: number
  level: 'ok' | 'low' | 'empty' | 'off'
  low_at_trials: number
  running: number
  min_topup_trials: number
  topup_options: { trials: number; amount_minor: number }[]
  auto_topup: boolean
  auto_topup_trials: number | null
}

export interface Eligibility {
  can_publish: boolean
  reasons: { code: string; message: string; fix: string | null }[]
  plan: string
  plan_label: string
  is_pro: boolean
  live_at: number | null
}

export interface Listing {
  id: number
  slug: string
  status: 'draft' | 'in_review' | 'changes_requested' | 'live' | 'paused' | 'removed'
  owner_user_id: string
  project: string
  currency: string
  name: string | null
  tagline: string | null
  category: string | null
  description: string | null
  support_email: string | null
  icon_color: string | null
  screenshots: { id: string; caption: string; url: string }[]
  pricing: Pricing
  evaluation: {
    currency: string
    fee_percent: number
    person: Judgement | null
    server: Record<string, Judgement>
    per_person_cost_minor: number
    server_costs: Record<string, number>
    per_trial_cost_minor: number
    errors: string[]
  }
  server_sizes: { id: string; name: string; max_people: number; hosting_cost_minor: number; storage_gb: number; files_gb: number }[]
  trial_rules: { allowed_days: number[]; max_days: number; max_people: number; people_options: number[]; min_topup_trials: number; topup_trial_options: number[]; low_budget_trials: number }
  trial_budget: TrialBudget
  categories: string[]
  problems: string[]
  unreviewed_changes: boolean
  is_published: boolean
  submitted_at: string | null
  review_due_at: string | null
  reviewed_at: string | null
  review_note: string | null
  published_at: string | null
  review_days: { first_review_business_days: number; update_review_business_days: number }
  notice_days: number
  installs: number
  store_url: string
  eligibility?: Eligibility
  publisher?: string
  overdue?: boolean
}

export interface PublishableProject {
  owner_user_id: string
  project: string
  title: string
  live: boolean
  listing_status: Listing['status'] | null
  listing_id: number | null
}

export async function getSellProjects(): Promise<{ enabled: boolean; is_pro: boolean; plan: string; plan_label: string; projects: PublishableProject[] }> {
  const { data } = await api.get(`${BASE}/sell/projects`)
  return data
}

export async function createListing(project: string, owner_user_id?: string): Promise<Listing> {
  const { data } = await api.post(`${BASE}/sell/listings`, { project, owner_user_id })
  return data
}

export async function getListing(id: number): Promise<Listing> {
  const { data } = await api.get(`${BASE}/sell/listings/${id}`)
  return data
}

export async function updateListing(id: number, body: Partial<Pick<Listing, 'name' | 'tagline' | 'category' | 'description' | 'support_email' | 'icon_color'>> & { pricing?: Pricing }): Promise<Listing> {
  const { data } = await api.put(`${BASE}/sell/listings/${id}`, body)
  return data
}

export async function submitListing(id: number): Promise<Listing> {
  const { data } = await api.post(`${BASE}/sell/listings/${id}/submit`)
  return data
}

export async function pauseListing(id: number, paused: boolean): Promise<Listing> {
  const { data } = await api.post(`${BASE}/sell/listings/${id}/${paused ? 'pause' : 'resume'}`)
  return data
}

export async function removeListing(id: number): Promise<Listing> {
  const { data } = await api.post(`${BASE}/sell/listings/${id}/remove`)
  return data
}

export async function uploadScreenshot(id: number, file: File, caption?: string): Promise<Listing> {
  const fd = new FormData()
  fd.append('file', file)
  if (caption) fd.append('caption', caption)
  const { data } = await api.post(`${BASE}/sell/listings/${id}/screenshots`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function deleteScreenshot(id: number, shotId: string): Promise<Listing> {
  const { data } = await api.delete(`${BASE}/sell/listings/${id}/screenshots/${shotId}`)
  return data
}

export async function budgetCheckout(id: number, trials: number): Promise<CheckoutOrder> {
  const { data } = await api.post(`${BASE}/sell/listings/${id}/trial-budget/checkout`, { trials })
  return data
}

export async function budgetFromWallet(id: number, trials: number): Promise<Listing> {
  const { data } = await api.post(`${BASE}/sell/listings/${id}/trial-budget/from-wallet`, { trials })
  return data
}

export async function budgetWithdraw(id: number) {
  const { data } = await api.post(`${BASE}/sell/listings/${id}/trial-budget/withdraw`)
  return data as { moved_minor: number; listing: Listing }
}

export async function budgetAuto(id: number, enabled: boolean, trials?: number): Promise<Listing> {
  const { data } = await api.put(`${BASE}/sell/listings/${id}/trial-budget/auto`, { enabled, trials })
  return data
}

export interface SellerCustomer {
  id: number
  organization: string
  status: Subscription['status']
  plan: string
  since: string | null
  trial_ends_at: string | null
  month_paid_minor: number
  month_fee_minor: number
  month_hosting_minor: number
  month_earn_minor: number
  trial_used_minor: number
  trial_reserved_minor: number
  trial_released: boolean
  converted: boolean
  people: number
}

export async function getSellerDashboard(): Promise<{
  listings: { listing: Listing; title: string; customers: SellerCustomer[]; app_wallet_minor: number; app_wallet_currency: string }[]
  month_totals: Record<string, { amount: number; fee: number; hosting: number; earn: number }>
  fee_percent: number
  notice_days: number
}> {
  const { data } = await api.get(`${BASE}/sell/dashboard`)
  return data
}

// ── instance admin ──────────────────────────────────────────────────────────

export interface SizeCost {
  id: string
  name: string
  max_people: number
  machines: { app: string; database: string }
  market_reference: Record<string, string>
  storage_gb: number
  files_gb: number
  limits: Record<string, number>
  server_cost_minor: number
  management_minor: number
  margin_minor: number
  hosting_cost_minor: number
  currency: string
}

export interface AdminPolicy {
  effective: Record<string, any>
  defaults: Record<string, any>
  overrides: Record<string, any>
  sizes: Record<string, SizeCost[]>
  impact: { listing: string; size: string; price_minor: number; cost_minor: number; currency: string }[]
}

export async function getAdminPolicy(): Promise<AdminPolicy> {
  const { data } = await api.get(`${BASE}/admin/policy`)
  return data
}

export async function previewAdminPolicy(overrides: Record<string, any>): Promise<AdminPolicy> {
  const { data } = await api.post(`${BASE}/admin/policy/preview`, { overrides })
  return data
}

export async function saveAdminPolicy(body: { overrides: Record<string, any>; marketplace_fee_percent?: number; price_change_notice_days?: number }): Promise<AdminPolicy> {
  const { data } = await api.put(`${BASE}/admin/policy`, body)
  return data
}

export async function getAdminListings(status?: string): Promise<{ listings: Listing[] }> {
  const { data } = await api.get(`${BASE}/admin/listings`, { params: { status } })
  return data
}

export async function reviewListing(id: number, approve: boolean, note?: string): Promise<Listing> {
  const { data } = await api.post(`${BASE}/admin/listings/${id}/review`, { approve, note })
  return data
}

export async function takedownListing(id: number): Promise<Listing> {
  const { data } = await api.post(`${BASE}/admin/listings/${id}/takedown`)
  return data
}

// ── Get it built: standard payment terms (docs/marketplace_get_it_built.md) ──

export interface BuildStage { key: string; name: string; approves?: string; pct: number }

export interface BuildTerms {
  stages: BuildStage[]
  advance_release: 'first_approval' | 'builder_accepts'
  collect_on: 'submit' | 'approve'
  approve_days: number
  on_no_response: 'auto_approve' | 'support'
  reminder_days_before: number[]
  release_delay_hours: number
  signoff_min_pct: number
  max_stages: number
  min_stage_minor: Record<string, number>
  client_fee_percent: number
  builder_fee_percent: number
  builder_fee_tax: 'added' | 'included'
  tds_percent: Record<string, number>
  accept_window_hours: number
  quote_valid_days: number
  fix_window_days: number
}

export interface BuildExampleRow {
  index: number
  key: string
  name: string
  approves: string
  pct: number
  amount_minor: number
  client: { base_minor: number; service_fee_minor: number; total_minor: number; service_fee_percent: number }
  builder: { gross_minor: number; fee_minor: number; fee_tax_minor: number; tds_minor: number; net_minor: number }
}

export interface BuildTermsView {
  terms: BuildTerms
  defaults: BuildTerms
  override: Partial<BuildTerms>
  currency: string
  example_minor: number
  example: BuildExampleRow[]
}

export async function getPublicBuildTerms(exampleMinor?: number): Promise<{ terms: BuildTerms; currency: string; example: BuildExampleRow[] }> {
  const { data } = await api.get(`${BASE}/public/build-terms`, { params: { example_minor: exampleMinor } })
  return data
}

export async function getAdminBuildTerms(exampleMinor?: number): Promise<BuildTermsView> {
  const { data } = await api.get(`${BASE}/admin/build-terms`, { params: { example_minor: exampleMinor } })
  return data
}

export async function previewAdminBuildTerms(terms: Partial<BuildTerms>, exampleMinor?: number): Promise<BuildTermsView> {
  const { data } = await api.post(`${BASE}/admin/build-terms/preview`, { terms, example_minor: exampleMinor })
  return data
}

/** Save the override (an empty object resets to Twasta's defaults). */
export async function saveAdminBuildTerms(terms: Partial<BuildTerms>, exampleMinor?: number): Promise<BuildTermsView> {
  const { data } = await api.put(`${BASE}/admin/build-terms`, { terms, example_minor: exampleMinor })
  return data
}

/** A marketplace error's plain message (+ code and extra fields). */
export function mktError(e: unknown, fallback = 'Something went wrong. Please try again.'): { message: string; code?: string; extra: Record<string, any> } {
  const d = (e as { response?: { data?: any } })?.response?.data
  const detail = d?.detail ?? d
  if (detail && typeof detail === 'object' && detail.message) {
    const { message, code, ...extra } = detail
    return { message, code, extra }
  }
  if (typeof detail === 'string') return { message: detail, extra: {} }
  return { message: fallback, extra: {} }
}

/** ₹2,490 / ₹2,938.20 / $29.88 — whole amounts show no decimals. */
export function money(minor: number | null | undefined, currency: string | null | undefined): string {
  if (minor === null || minor === undefined) return '—'
  const neg = minor < 0
  const v = Math.abs(minor) / 100
  const locale = currency === 'INR' ? 'en-IN' : 'en-US'
  const whole = Number.isInteger(v)
  const txt = v.toLocaleString(locale, { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 })
  const sym = currency === 'INR' ? '₹' : currency === 'USD' ? '$' : `${currency ?? ''} `
  return `${neg ? '−' : ''}${sym}${txt}`
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}
